import { Link } from 'react-router';
import { buildMeta, breadcrumbSchema, faqSchema } from '~/lib/seo';
import { SITE } from '~/data/site';
import { Estimator } from '~/components/marketing/estimator';
import {
  RATE_CARD,
  FACILITY_LABELS,
  FREQUENCY_TIERS,
  VISIT_MINIMUM,
  PROJECT_RATES,
  SALES_TAX,
  ALWAYS_INCLUDED,
  PRICED_SEPARATELY,
} from '@shared/pricing';
import { FACILITY_TYPES } from '@shared/types';
import { formatCurrency } from '@shared/utils';

const FAQ = [
  {
    q: 'Why does the price per square foot go down the more often you come?',
    a: 'A building that is cleaned nightly never gets dirty enough to need deep work. A building cleaned every other week does. The rate reflects how much work each visit actually is, not a volume discount.',
  },
  {
    q: 'Is there a minimum?',
    a: `Yes — ${formatCurrency(VISIT_MINIMUM)} a visit. Below about 1,100 square feet the minimum sets the price rather than the square footage, because the crew still has to arrive, work, and close out.`,
  },
  {
    q: 'Do I have to sign an annual contract?',
    a: 'No. Month to month, 30 days notice either way, no early-termination fee. Roughly 96% of janitorial contracts already carry a 30-day clause — most vendors just do not say so.',
  },
  {
    q: 'Is sales tax included in these prices?',
    a: `No. Commercial cleaning is a taxable service in Florida — 6% state plus the county surtax, so ${(SALES_TAX.counties[0].rate * 100).toFixed(1)}% in ${SALES_TAX.counties[0].county} and ${(SALES_TAX.counties[1].rate * 100).toFixed(1)}% in ${SALES_TAX.counties[1].county}. Residential cleaning is exempt; commercial is not. It appears as its own line on the invoice.`,
  },
  {
    q: 'Are supplies included?',
    a: 'Our equipment and chemicals, yes — always. Consumables you restock for your own people, like paper and soap, are normally yours, but we will carry them and bill at cost if you prefer.',
  },
  {
    q: 'Will the price change after I sign?',
    a: 'The walkthrough price is the price. If the scope changes — you take another suite, you add a floor — we requote that part in writing before it happens.',
  },
];

export function meta() {
  return buildMeta({
    title: 'Commercial cleaning prices, published',
    description:
      'Real per-square-foot rates for office, retail, medical and industrial cleaning in Tampa Bay. Frequency tiers, a $175 visit minimum, and the sales tax line.',
    path: '/pricing',
    jsonLd: [
      breadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Pricing', url: '/pricing' },
      ]),
      faqSchema(FAQ),
    ],
  });
}

export default function Pricing() {
  return (
    <>
      <section className="hero dusk grain">
        <div className="shell">
          <div className="hero-inner">
            <p className="eyebrow-pill">Pricing</p>
            <h1>Straight answers on cost.</h1>
            <p className="lede">
              We checked seventeen commercial cleaning companies across Tampa Bay. Not one publishes
              a price. Here are ours.
            </p>
          </div>
        </div>
      </section>

      {/* ── The estimate widget ────────────────────────────────────────── */}
      <section className="section-tight">
        <div className="shell">
          <div className="split">
            <div className="stack">
              <h2>What would my building cost?</h2>
              <p>
                Move the slider. The number updates as you go — no email address, no &ldquo;request
                a quote to see pricing.&rdquo;
              </p>
              <p className="small">
                It is an estimate off the published rate. The real number comes from twenty minutes
                in your building, because the thing that moves a price is what is actually in the
                space, not its footprint.
              </p>
            </div>
            <div>
              <Estimator />
            </div>
          </div>
        </div>
      </section>

      {/* ── The rate card ──────────────────────────────────────────────── */}
      <section className="section section-ground">
        <div className="shell">
          <div className="section-head">
            <p className="eyebrow">The rate card</p>
            <h2>Per square foot, per visit.</h2>
            <p>
              Frequency tiers, not quality tiers. There is no version of this where we clean your
              building worse for less money.
            </p>
          </div>

          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th scope="col">Facility type</th>
                  {FREQUENCY_TIERS.map((t) => (
                    <th key={t.key} scope="col" className="numeric">
                      {t.label}
                      <br />
                      <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                        {t.cadence}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FACILITY_TYPES.filter((t) => RATE_CARD[t]).map((t) => {
                  const rates = RATE_CARD[t]!;
                  return (
                    <tr key={t}>
                      <th scope="row">{FACILITY_LABELS[t]}</th>
                      {FREQUENCY_TIERS.map((tier) => (
                        <td key={tier.key} className="numeric">
                          ${rates[tier.key].toFixed(2)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="stack" style={{ marginTop: '1.5rem' }}>
            <div className="callout">
              <p>
                <strong>Minimum {formatCurrency(VISIT_MINIMUM)} per visit.</strong> Under roughly
                1,100 square feet the minimum sets the price — the crew still has to arrive, work
                the checklist, and close the building out.
              </p>
            </div>

            <p className="small">
              A worked example: a 5,000 sq ft office cleaned three nights a week is $0.15 × 5,000 ={' '}
              <strong>$750 a visit</strong>, about $9,750 a month. Nightly it drops to $0.13 —{' '}
              <strong>$650 a visit</strong> — because a building cleaned every night never gets
              dirty enough to need the deeper work.
            </p>
          </div>
        </div>
      </section>

      {/* ── One-off work ───────────────────────────────────────────────── */}
      <section className="section">
        <div className="shell">
          <div className="section-head">
            <p className="eyebrow">Project work</p>
            <h2>One-off jobs, priced separately.</h2>
          </div>

          <div className="grid-2">
            {Object.values(PROJECT_RATES).map((p) => (
              <div key={p.label} className="card">
                <h4>{p.label}</h4>
                <p className="proof-figure">${p.rate.toFixed(2)}/sq ft</p>
                <p>{p.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Included vs extra ──────────────────────────────────────────── */}
      <section className="section section-ground">
        <div className="shell">
          <div className="split">
            <div className="stack">
              <p className="eyebrow">Always included</p>
              <h2>In every visit, at every frequency.</h2>
              <ul className="check">
                {ALWAYS_INCLUDED.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="stack">
              <p className="eyebrow">Priced separately</p>
              <h2>Named up front, so nothing reads as a hidden fee.</h2>
              <ul className="dash">
                {PRICED_SEPARATELY.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <p className="small">
                These are real work with real cost. Burying them in a headline rate means either the
                rate is inflated or the work is not getting done.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Terms ──────────────────────────────────────────────────────── */}
      <section className="section">
        <div className="shell-narrow stack-lg">
          <div className="stack">
            <p className="eyebrow">Terms</p>
            <h2>No long-term contract.</h2>
            <p>
              Month to month, 30 days notice either way, no early-termination fee. Our equipment and
              chemicals are included. A certificate of insurance goes out on request
              {SITE.claims.coiWithin24h ? ' within 24 hours' : ''}.
            </p>
            <p>
              Invoiced monthly, net 30. Florida sales tax is a separate line — it is a real tax on a
              real service, not a surcharge we invented.
            </p>
          </div>

          <div>
            <h3 style={{ marginBottom: '0.5rem' }}>Sales tax by county</h3>
            <p className="small" style={{ marginBottom: '1rem' }}>
              6% state plus the county discretionary surtax. Rates as of {SALES_TAX.asOf}.
            </p>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th scope="col">County</th>
                    <th scope="col" className="numeric">
                      Rate
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {SALES_TAX.counties.map((c) => (
                    <tr key={c.county}>
                      <th scope="row">{c.county}</th>
                      <td className="numeric">{(c.rate * 100).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="faq">
            <h3 style={{ marginBottom: '1rem' }}>Questions we get about price</h3>
            {FAQ.map((item) => (
              <details key={item.q}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="dawn">
        <div className="shell">
          <div className="cta-inner">
            <h2>Want the exact number for your building?</h2>
            <p>Twenty minutes on site and you get a fixed price in writing.</p>
            <div className="btn-row">
              <Link to="/quote" className="btn btn-primary">
                Book a walkthrough
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
